import { Button } from 'designSystem/components/Button'
import { Checkbox } from 'designSystem/components/Checkbox'
import { CleaningControls } from 'features/timeSeries/experiment/CleaningControls'
import { CollapsibleSection } from 'features/timeSeries/experiment/CollapsibleSection'
import { CorrelationCard } from 'features/timeSeries/experiment/CorrelationCard'
import { DiagnosticsPanel } from 'features/timeSeries/experiment/DiagnosticsPanel'
import { useExperimentLab } from 'features/timeSeries/experiment/ExperimentLabContext'
import {
  KNOB_HELP,
  METHOD_HELP,
  METHOD_LABELS,
} from 'features/timeSeries/experiment/knobCopy'
import { KnobHelp } from 'features/timeSeries/experiment/KnobHelp'
import { NormalizationViewer } from 'features/timeSeries/experiment/NormalizationViewer'
import { ResidualMiniPlot } from 'features/timeSeries/experiment/ResidualMiniPlot'
import { RunComparison } from 'features/timeSeries/experiment/RunComparison'
import {
  prepareRunsForSave,
  restoreExperimentRuns,
} from 'features/timeSeries/experiment/runPersistence'
import { SizedPlot } from 'features/timeSeries/experiment/SizedPlot'
import { SplitBuilder } from 'features/timeSeries/experiment/SplitBuilder'
import { TagLabel } from 'features/timeSeries/experiment/TagLabel'
import { TagPicker } from 'features/timeSeries/experiment/TagPicker'
import { TransparencyStrip } from 'features/timeSeries/experiment/TransparencyStrip'
import {
  BandEstimator,
  EXPERIMENT_METHOD_KEYS,
  ExperimentConfig,
  ExperimentMethodKey,
  formatSavedExperimentLabel,
  IntervalUnit,
  mergeLoadedConfig,
  SamplingMode,
} from 'features/timeSeries/experiment/types'
import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import { PlotData } from 'plotly.js'
import { FC, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'

const FG = CantileverTheme.colors.grayscale['100']
const GRID = CantileverTheme.colors.grayscale['600']

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.25rem 0.5rem 0.75rem;
  font-size: 0.8rem;
`

const Hint = styled.p`
  margin: 0;
  font-size: 0.72rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const Group = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`

const GroupTitle = styled.div`
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  font-size: 0.72rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const Input = styled.input`
  padding: 0.3rem 0.45rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.forecast.pageBackground1};
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.78rem;
`

const Select = styled.select`
  padding: 0.3rem 0.45rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.forecast.pageBackground1};
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.78rem;
`

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  align-items: center;
`

const Tabs = styled.div`
  display: flex;
  gap: 0.35rem;
  flex-wrap: wrap;
`

const TabBtn = styled.button<{ $active: boolean }>`
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.colors.blue['400'] : theme.forecast.border};
  border-radius: 0.375rem;
  padding: 0.35rem 0.85rem;
  background: ${({ theme, $active }) =>
    $active ? theme.forecast.background2 : theme.forecast.pageBackground1};
  color: ${({ theme }) => theme.forecast.defaultText};
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
`

const OffenderList = styled.ul`
  margin: 0.25rem 0 0;
  padding-left: 1.1rem;
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.forecast.secondaryText};

  li {
    margin-bottom: 0.15rem;
  }
`

const CheckRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.75rem;
  cursor: pointer;
`

const Error = styled.div`
  color: #ef4444;
  font-size: 0.78rem;
`

const ProgressLog = styled.div`
  max-height: 10rem;
  overflow-y: auto;
  font-size: 0.7rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 6px;
  padding: 0.4rem 0.5rem;
  background: ${({ theme }) => theme.forecast.pageBackground1};
  color: ${({ theme }) => theme.forecast.secondaryText};
  white-space: pre-wrap;
`

const KnobGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(15rem, 1fr));
  gap: 0.85rem 1.1rem;
`

const KnobPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  padding: 0.7rem 0.8rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.forecast.background2};
`

const TagStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const YRangeTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.72rem;

  th,
  td {
    text-align: left;
    padding: 0.25rem 0.35rem;
    border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
    vertical-align: middle;
  }

  th {
    color: ${({ theme }) => theme.forecast.secondaryText};
    font-weight: 500;
  }

  input {
    width: 5.5rem;
  }
`

const short = (id: string) => id.split('-').slice(1).join('-') || id

type LabTab = 'data' | 'model'

/**
 * The Experiment Lab, rehosted as a Process Data sidebar tab. Time-series
 * surfaces (cleaned pens, removed points, expected/residual band) render on
 * the main viewer chart via pens + uPlot overlays; this tab keeps the config
 * knobs and the non-time-indexed diagnostics (loss curve, correlation vs
 * shift, residual histogram, kNN scatter) as Plotly cards. All session state
 * lives in ExperimentLabContext so tab switches don't lose a trained run.
 */
export const ExperimentTab: FC = () => {
  const { siteID, timeRange } = useScatterplotContext()
  const {
    config,
    setConfig,
    patchConfig,
    api,
    runs,
    setRuns,
    selectedMethodKey,
    setSelectedMethodKey,
    showOverlays,
    setShowOverlays,
    targetOnlyView,
    setTargetOnlyView,
    outOfBandPoints,
    saveName,
    setSaveName,
    savedEntryId,
    setSavedEntryId,
    lagsText,
    setLagsText,
    hiddenText,
    setHiddenText,
    useViewerRange,
    resetViewerToExperimentWindow,
    markEngaged,
  } = useExperimentLab()

  const [tab, setTab] = useState<LabTab>('data')
  const [selectedListId, setSelectedListId] = useState('')
  const [includeModels, setIncludeModels] = useState(false)
  const [saveMessage, setSaveMessage] = useState<string | null>(null)

  // Opening the tab engages the session: the provider's preview fetch and
  // date-window push stay dormant until a user has actually been here.
  useEffect(() => {
    markEngaged()
  }, [markEngaged])

  const { listSaved } = api
  useEffect(() => {
    void listSaved()
  }, [listSaved, siteID])

  // Keep the saved-list select pointed at a valid entry (prefer editing id).
  useEffect(() => {
    setSelectedListId((prev) => {
      if (!api.savedList.length) return ''
      if (prev && api.savedList.some((x) => x.entry_id === prev)) return prev
      if (
        savedEntryId &&
        api.savedList.some((x) => x.entry_id === savedEntryId)
      ) {
        return savedEntryId
      }
      return api.savedList[0].entry_id
    })
  }, [api.savedList, savedEntryId])

  const splitChunks = api.preview?.split_chunks ?? []

  const studioTags = useMemo(
    () => [config.target, ...config.drivers].filter(Boolean),
    [config.target, config.drivers]
  )

  const onTrain = async () => {
    setTab('model')
    const result = await api.train(config)
    if (!result?.ok) return
    setRuns((prev) => [
      {
        id: `${Date.now()}`,
        createdAt: new Date().toISOString(),
        label: `Run ${prev.length + 1}`,
        config: structuredClone(config),
        result,
      },
      ...prev,
    ])
  }

  const onSave = async (asNew = false) => {
    const name =
      saveName.trim() ||
      (config.target ? short(config.target) : '') ||
      'experiment'
    const persistedRuns = prepareRunsForSave(runs, config, api.trainResult)
    setRuns(persistedRuns)
    setSaveMessage(null)
    const res = await api.saveExperiment({
      name,
      config,
      result: api.trainResult,
      runs: persistedRuns,
      notes: config.notes || '',
      entryId: asNew ? null : savedEntryId,
      includeModels,
    })
    if (!res) return
    setSavedEntryId(res.entry_id)
    setSaveName(res.name)
    setSaveMessage(
      res.warnings?.length
        ? `Saved (${res.warnings.join('; ')})`
        : `Saved as ${res.name}`
    )
  }

  const onLoadSaved = async (entryId: string) => {
    const loaded = await api.loadExperiment(entryId)
    if (!loaded?.config) return
    const next = mergeLoadedConfig(loaded.config)
    const restoredRuns = restoreExperimentRuns(loaded, next)
    setConfig(next)
    setRuns(restoredRuns)
    setLagsText((next.feature.driver_lags || []).join(','))
    setHiddenText((next.mlp_hidden_layer_sizes || []).join(','))
    setSavedEntryId(loaded.entry_id)
    setSaveName(loaded.name || '')
    const restoredResult =
      loaded.result?.ok && loaded.result
        ? loaded.result
        : restoredRuns.find((run) => run.result.ok)?.result || null
    if (restoredResult) {
      api.setTrainResult(restoredResult)
      setTab('model')
    } else {
      api.setTrainResult(null)
      setTab('data')
    }
    const warnings = loaded.warnings?.join('; ')
    setSaveMessage(
      `Loaded ${loaded.name || entryId}${warnings ? ` (${warnings})` : ''}`
    )
  }

  const onDeleteSelected = async () => {
    if (!selectedListId) return
    const item = api.savedList.find((x) => x.entry_id === selectedListId)
    const label = item?.name || selectedListId
    if (!window.confirm(`Delete saved experiment "${label}"?`)) return
    const ok = await api.deleteExperiment(selectedListId)
    if (!ok) return
    if (savedEntryId === selectedListId) {
      setSavedEntryId(null)
      setSaveMessage('Deleted current save')
    } else {
      setSaveMessage(`Deleted ${label}`)
    }
  }

  const onSuggestCleaning = async () => {
    const res = await api.suggestCleaning(config)
    if (!res?.cleaning_plan) return
    patchConfig({ cleaning_plan: res.cleaning_plan })
  }

  const setYBound = (tag: string, bound: 'min' | 'max', raw: string) => {
    const value = raw.trim() === '' ? undefined : Number(raw)
    if (value !== undefined && !Number.isFinite(value)) return
    const prev = config.y_ranges || {}
    const entry = { ...prev[tag], [bound]: value }
    const next = { ...prev }
    if (entry.min === undefined && entry.max === undefined) {
      delete next[tag]
    } else {
      next[tag] = entry
    }
    patchConfig({ y_ranges: next })
  }

  const toggleMethod = (key: ExperimentMethodKey) => {
    const has = config.methods.includes(key)
    patchConfig({
      methods: has
        ? config.methods.filter((m) => m !== key)
        : [...config.methods, key],
    })
  }

  return (
    <Wrap>
      <Hint>
        The chart&apos;s pens and the experiment&apos;s tags are the same set:
        every pen on the chart (including ones already there) joins the
        experiment — the first as target, the rest as drivers — and
        target/driver tags picked here plot as pens. Removing either side
        removes both. Removed points and the trained expected/residual band draw
        over the pens, with the chart&apos;s zoom, tooltip, and ruler.
        Diagnostics live here.
      </Hint>

      <Row>
        <CheckRow>
          <Checkbox
            checked={showOverlays}
            onCheckedChange={(v) => setShowOverlays(!!v)}
          />
          Draw experiment overlays on the chart
        </CheckRow>
        <CheckRow>
          <Checkbox
            checked={targetOnlyView}
            onCheckedChange={(v) => setTargetOnlyView(!!v)}
          />
          Target only (hide driver pens)
        </CheckRow>
      </Row>

      <CollapsibleSection
        sectionKey='savedExperiments'
        title='Saved experiments'
        defaultOpen={false}>
        <Field>
          Name
          <Input
            value={saveName}
            placeholder={
              config.target ? short(config.target) : 'experiment name'
            }
            onChange={(e) => setSaveName(e.target.value)}
          />
        </Field>
        <CheckRow>
          <Checkbox
            checked={includeModels}
            onCheckedChange={(v) => setIncludeModels(!!v)}
          />
          Also save model weights (slower re-fit)
        </CheckRow>
        <Row>
          <Button
            disabled={
              !config.target || !config.drivers.length || api.saveLoading
            }
            onClick={() => void onSave(false)}>
            {api.saveLoading
              ? 'Saving…'
              : savedEntryId
                ? 'Update save'
                : 'Save'}
          </Button>
          {savedEntryId && (
            <Button
              variant='outline'
              disabled={api.saveLoading}
              onClick={() => void onSave(true)}>
              Save as new
            </Button>
          )}
        </Row>
        {api.saveError && <Error>{api.saveError}</Error>}
        {saveMessage && <Hint>{saveMessage}</Hint>}
        {savedEntryId && (
          <Hint>
            Editing: {saveName || 'untitled'} ({savedEntryId})
          </Hint>
        )}
        {api.savedError && <Error>{api.savedError}</Error>}
        {api.savedList.length === 0 && !api.savedLoading ? (
          <Hint>No saved experiments</Hint>
        ) : (
          <>
            <Field>
              Saved
              <Select
                value={selectedListId}
                disabled={!api.savedList.length || api.savedLoading}
                onChange={(e) => setSelectedListId(e.target.value)}>
                {api.savedList.map((item) => (
                  <option key={item.entry_id} value={item.entry_id}>
                    {formatSavedExperimentLabel(item)}
                  </option>
                ))}
              </Select>
            </Field>
            <Row>
              <Button
                variant='outline'
                disabled={!selectedListId || api.loadLoading}
                onClick={() => void onLoadSaved(selectedListId)}>
                {api.loadLoading ? 'Loading…' : 'Load'}
              </Button>
              <Button
                variant='outline'
                disabled={!selectedListId}
                onClick={() => void onDeleteSelected()}>
                Delete
              </Button>
              <Button
                variant='outline'
                disabled={api.savedLoading}
                onClick={() => void listSaved()}>
                {api.savedLoading ? 'Refreshing…' : 'Refresh'}
              </Button>
            </Row>
          </>
        )}
      </CollapsibleSection>

      <CollapsibleSection sectionKey='tags' title='Target / Drivers'>
        <TagPicker
          siteId={siteID}
          label={<KnobHelp label='Target' help={KNOB_HELP.target} />}
          value={config.target ? [config.target] : []}
          onChange={(ids) => patchConfig({ target: ids[0] || '' })}
          exclude={config.drivers}
        />
        <TagPicker
          siteId={siteID}
          label={<KnobHelp label='Drivers' help={KNOB_HELP.drivers} />}
          value={config.drivers}
          onChange={(ids) => patchConfig({ drivers: ids })}
          multi
          exclude={config.target ? [config.target] : []}
        />
      </CollapsibleSection>

      <CollapsibleSection
        sectionKey='yRanges'
        title='Y axis ranges'
        defaultOpen={false}>
        <Hint>
          Pin a tag&apos;s chart Y range (custom bounds win over auto). Applies
          to the viewer&apos;s pens immediately and saves with the experiment.
          Leave a field empty for auto.
        </Hint>
        {studioTags.length === 0 ? (
          <Hint>Pick a target and drivers above.</Hint>
        ) : (
          <YRangeTable>
            <thead>
              <tr>
                <th>Tag</th>
                <th>Y min</th>
                <th>Y max</th>
              </tr>
            </thead>
            <tbody>
              {studioTags.map((tag) => (
                <tr key={tag}>
                  <td>
                    <TagLabel tagId={tag}>
                      <span>
                        {short(tag)}
                        {tag === config.target ? ' · target' : ''}
                      </span>
                    </TagLabel>
                  </td>
                  <td>
                    <Input
                      type='number'
                      step='any'
                      placeholder='auto'
                      value={config.y_ranges?.[tag]?.min ?? ''}
                      onChange={(e) => setYBound(tag, 'min', e.target.value)}
                    />
                  </td>
                  <td>
                    <Input
                      type='number'
                      step='any'
                      placeholder='auto'
                      value={config.y_ranges?.[tag]?.max ?? ''}
                      onChange={(e) => setYBound(tag, 'max', e.target.value)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </YRangeTable>
        )}
      </CollapsibleSection>

      <CollapsibleSection sectionKey='dateWindow' title='Date window'>
        <Row>
          <Field>
            <KnobHelp label='Start' help={KNOB_HELP.startDate} />
            <Input
              type='date'
              value={config.start_date}
              onChange={(e) => patchConfig({ start_date: e.target.value })}
            />
          </Field>
          <Field>
            <KnobHelp label='End' help={KNOB_HELP.endDate} />
            <Input
              type='date'
              value={config.end_date}
              onChange={(e) => patchConfig({ end_date: e.target.value })}
            />
          </Field>
        </Row>
        <Row>
          <Button variant='outline' onClick={useViewerRange}>
            Use chart range
          </Button>
          <Button
            variant='outline'
            disabled={!config.start_date || !config.end_date}
            onClick={resetViewerToExperimentWindow}>
            Reset chart to window
          </Button>
          <Hint>
            Chart shows {timeRange.from.slice(0, 10)} →{' '}
            {timeRange.to.slice(0, 10)}. Changing the dates above moves the
            chart to match.
          </Hint>
        </Row>
        <Field>
          <KnobHelp label='Sampling mode' help={KNOB_HELP.samplingMode} />
          <Select
            value={config.sampling_mode}
            onChange={(e) =>
              patchConfig({ sampling_mode: e.target.value as SamplingMode })
            }>
            <option value='aggregate'>
              Aggregate onto grid (prod default)
            </option>
            <option value='raw'>Raw historian points (all available)</option>
          </Select>
        </Field>
        {config.sampling_mode === 'aggregate' && (
          <>
            <Row>
              <Field style={{ flex: 1 }}>
                <KnobHelp label='Interval' help={KNOB_HELP.interval} />
                <Input
                  type='number'
                  min={1}
                  value={config.interval}
                  onChange={(e) => {
                    const n = Number(e.target.value) || 10
                    patchConfig({
                      interval: n,
                      interval_minutes:
                        config.interval_unit === 'minutes'
                          ? n
                          : config.interval_minutes,
                    })
                  }}
                />
              </Field>
              <Field>
                <KnobHelp label='Unit' help={KNOB_HELP.intervalUnit} />
                <Select
                  value={config.interval_unit}
                  onChange={(e) => {
                    const unit = e.target.value as IntervalUnit
                    patchConfig({
                      interval_unit: unit,
                      interval_minutes:
                        unit === 'minutes'
                          ? config.interval
                          : config.interval_minutes,
                    })
                  }}>
                  <option value='seconds'>seconds</option>
                  <option value='minutes'>minutes</option>
                  <option value='hours'>hours</option>
                </Select>
              </Field>
            </Row>
            <Field>
              <KnobHelp label='Aggregation' help={KNOB_HELP.aggregation} />
              <Select
                value={config.aggregation_op}
                onChange={(e) =>
                  patchConfig({
                    aggregation_op: e.target
                      .value as ExperimentConfig['aggregation_op'],
                  })
                }>
                <option value='mean'>mean</option>
                <option value='median'>median</option>
                <option value='min'>min</option>
                <option value='max'>max</option>
                <option value='last_value'>last_value</option>
              </Select>
            </Field>
            <Field>
              <KnobHelp label='Gap fill' help={KNOB_HELP.gapFill} />
              <Select
                value={config.gap_fill}
                onChange={(e) =>
                  patchConfig({
                    gap_fill: e.target.value as ExperimentConfig['gap_fill'],
                  })
                }>
                <option value='off'>off (prod default)</option>
                <option value='locf'>locf</option>
              </Select>
            </Field>
          </>
        )}
        {config.sampling_mode === 'raw' && (
          <Hint>
            Raw mode pulls every historian point and only aligns tags when
            timestamps match exactly. Prefer a shorter date range — dense tags
            can be huge.
          </Hint>
        )}
      </CollapsibleSection>

      <Tabs>
        {(
          [
            ['data', 'Data Studio'],
            ['model', 'Model Studio'],
          ] as const
        ).map(([id, label]) => (
          <TabBtn
            key={id}
            type='button'
            $active={tab === id}
            onClick={() => setTab(id)}>
            {label}
          </TabBtn>
        ))}
      </Tabs>

      {tab === 'data' && (
        <>
          <TransparencyStrip
            funnel={api.preview?.funnel ?? null}
            alignment={
              api.preview?.alignment ?? api.preview?.funnel?.alignment ?? null
            }
            tagStats={api.preview?.tag_stats}
            loading={api.previewLoading}
            error={api.previewError}
          />

          {api.previewError && <Error>{api.previewError}</Error>}
          {api.previewLoading && !api.preview && (
            <Hint>Loading data preview…</Hint>
          )}

          <CollapsibleSection sectionKey='cleaning' title='Cleaning'>
            <Row>
              <Button
                variant='outline'
                disabled={
                  !config.target || !config.drivers.length || api.suggestLoading
                }
                onClick={() => void onSuggestCleaning()}>
                {api.suggestLoading ? 'Suggesting…' : 'Suggest (LLM)'}
              </Button>
              <KnobHelp
                label='About Suggest'
                help={KNOB_HELP.suggestCleaning}
              />
              <Button
                variant='outline'
                onClick={() =>
                  patchConfig({ cleaning_plan: { tags: {}, summary: '' } })
                }>
                Clear ops
              </Button>
              {api.previewLoading && api.preview && (
                <Hint>Refreshing preview…</Hint>
              )}
            </Row>
            {api.suggestError && <Error>{api.suggestError}</Error>}
            <CheckRow>
              <Checkbox
                checked={config.gate_scope !== 'train_only'}
                onCheckedChange={(v) =>
                  patchConfig({ gate_scope: v ? 'all' : 'train_only' })
                }
              />
              <KnobHelp
                label='Apply gate to test range'
                help={KNOB_HELP.gateScope}
              />
            </CheckRow>
            {config.gate_scope === 'train_only' && (
              <Hint>
                Gates only remove train/val rows — the test range keeps every
                row, so held-out metrics stay honest about all operating
                regimes.
              </Hint>
            )}
            <Hint>
              Plan tags: {Object.keys(config.cleaning_plan.tags || {}).length} ·{' '}
              {config.cleaning_plan.summary || 'no summary'}
            </Hint>

            <TagStack>
              {studioTags.length === 0 && (
                <Hint>Pick a target and drivers above.</Hint>
              )}
              {studioTags.map((tag) => (
                <CleaningControls
                  key={tag}
                  tag={tag}
                  isTarget={tag === config.target}
                  removed={api.preview?.removed_points?.[tag] ?? null}
                  tagStats={api.preview?.tag_stats?.[tag]}
                  rawStats={api.preview?.tag_stats_raw?.[tag]}
                  cleaningPlan={config.cleaning_plan}
                  onCleaningPlanChange={(cleaning_plan) =>
                    patchConfig({ cleaning_plan })
                  }
                />
              ))}
            </TagStack>
          </CollapsibleSection>

          <CollapsibleSection sectionKey='correlation' title='Correlation'>
            <CorrelationCard
              correlation={api.preview?.cross_correlation}
              drivers={config.drivers}
              phaseShifts={config.phase_shifts}
              corrShiftMax={config.corr_shift_max}
              corrShiftStep={config.corr_shift_step}
              interval={config.interval}
              intervalUnit={config.interval_unit}
              onCorrShiftMaxChange={(n) => patchConfig({ corr_shift_max: n })}
              onCorrShiftStepChange={(n) => patchConfig({ corr_shift_step: n })}
              onPhaseShiftsChange={(phase_shifts) =>
                patchConfig({ phase_shifts })
              }
            />
          </CollapsibleSection>

          <CollapsibleSection sectionKey='normalization' title='Normalization'>
            <NormalizationViewer
              siteId={siteID}
              config={config}
              result={api.normalizeResult}
              loading={api.normalizeLoading}
              error={api.normalizeError}
              onRun={(against, method) => {
                void api.normalize(config, against, method)
              }}
            />
          </CollapsibleSection>

          <CollapsibleSection
            sectionKey='cleaningJson'
            title='Advanced: cleaning plan JSON'
            defaultOpen={false}>
            <Field>
              <KnobHelp label='Edit plan JSON' help={KNOB_HELP.cleaningJson} />
              <textarea
                style={{
                  width: '100%',
                  minHeight: '7rem',
                  fontSize: '0.7rem',
                  fontFamily: 'monospace',
                }}
                value={JSON.stringify(config.cleaning_plan, null, 2)}
                onChange={(e) => {
                  try {
                    patchConfig({ cleaning_plan: JSON.parse(e.target.value) })
                  } catch {
                    /* ignore while typing */
                  }
                }}
              />
            </Field>
          </CollapsibleSection>
        </>
      )}

      {tab === 'model' && (
        <>
          <CollapsibleSection
            sectionKey='split'
            title={
              <KnobHelp label='Split builder' help={KNOB_HELP.splitBuilder} />
            }>
            <SplitBuilder
              split={config.split}
              chunks={splitChunks}
              onChange={(split) => patchConfig({ split })}
            />
          </CollapsibleSection>

          <CollapsibleSection sectionKey='knobs' title='Model knobs'>
            <KnobGrid>
              <KnobPanel>
                <GroupTitle>Features</GroupTitle>
                <Field>
                  <KnobHelp
                    label='Driver lags (comma-separated periods)'
                    help={KNOB_HELP.driverLags}
                  />
                  <Input
                    value={lagsText}
                    onChange={(e) => {
                      setLagsText(e.target.value)
                      const lags = e.target.value
                        .split(/[,\s]+/)
                        .map((x) => Number(x))
                        .filter((n) => Number.isFinite(n) && n > 0)
                      if (lags.length)
                        patchConfig({
                          feature: { ...config.feature, driver_lags: lags },
                        })
                    }}
                  />
                </Field>
                <Field>
                  <KnobHelp
                    label='Rolling window'
                    help={KNOB_HELP.rollingWindow}
                  />
                  <Input
                    type='number'
                    min={1}
                    value={config.feature.rolling_window}
                    onChange={(e) =>
                      patchConfig({
                        feature: {
                          ...config.feature,
                          rolling_window: Number(e.target.value) || 6,
                        },
                      })
                    }
                  />
                </Field>
                <Field>
                  <KnobHelp
                    label='Driver ffill limit'
                    help={KNOB_HELP.driverFfill}
                  />
                  <Input
                    type='number'
                    min={0}
                    value={config.feature.driver_ffill_limit}
                    onChange={(e) =>
                      patchConfig({
                        feature: {
                          ...config.feature,
                          driver_ffill_limit: Number(e.target.value) || 0,
                        },
                      })
                    }
                  />
                </Field>
                <CheckRow>
                  <Checkbox
                    checked={config.feature.include_driver_history}
                    onCheckedChange={(v) =>
                      patchConfig({
                        feature: {
                          ...config.feature,
                          include_driver_history: !!v,
                        },
                      })
                    }
                  />
                  <KnobHelp
                    label='Driver history (lags + rolling)'
                    help={KNOB_HELP.driverHistory}
                  />
                </CheckRow>
                <CheckRow>
                  <Checkbox
                    checked={config.feature.include_self_history}
                    onCheckedChange={(v) =>
                      patchConfig({
                        feature: {
                          ...config.feature,
                          include_self_history: !!v,
                        },
                      })
                    }
                  />
                  <KnobHelp
                    label='Self history (target lags)'
                    help={KNOB_HELP.selfHistory}
                  />
                </CheckRow>
              </KnobPanel>

              <KnobPanel>
                <GroupTitle>Model</GroupTitle>
                {EXPERIMENT_METHOD_KEYS.map((key) => (
                  <CheckRow key={key}>
                    <Checkbox
                      checked={config.methods.includes(key)}
                      onCheckedChange={() => toggleMethod(key)}
                    />
                    <KnobHelp
                      label={METHOD_LABELS[key] ?? key.replace('target_', '')}
                      help={METHOD_HELP[key] ?? ''}
                    />
                  </CheckRow>
                ))}
                <Field>
                  <KnobHelp
                    label='MLP hidden sizes'
                    help={KNOB_HELP.mlpHidden}
                  />
                  <Input
                    value={hiddenText}
                    onChange={(e) => {
                      setHiddenText(e.target.value)
                      const sizes = e.target.value
                        .split(/[,\s]+/)
                        .map((x) => Number(x))
                        .filter((n) => Number.isFinite(n) && n > 0)
                      if (sizes.length)
                        patchConfig({ mlp_hidden_layer_sizes: sizes })
                    }}
                  />
                </Field>
                <Field>
                  <KnobHelp label='MLP max iter' help={KNOB_HELP.mlpMaxIter} />
                  <Input
                    type='number'
                    value={config.mlp_max_iter}
                    onChange={(e) =>
                      patchConfig({
                        mlp_max_iter: Number(e.target.value) || 300,
                      })
                    }
                  />
                </Field>
                <Field>
                  <KnobHelp label='kNN k' help={KNOB_HELP.knnK} />
                  <Input
                    type='number'
                    value={config.knn_k}
                    onChange={(e) =>
                      patchConfig({ knn_k: Number(e.target.value) || 25 })
                    }
                  />
                </Field>
                <Field>
                  <KnobHelp
                    label='Band estimator'
                    help={KNOB_HELP.bandEstimator}
                  />
                  <Select
                    value={config.band_estimator}
                    onChange={(e) =>
                      patchConfig({
                        band_estimator: e.target.value as BandEstimator,
                      })
                    }>
                    <option value='iqr'>IQR (prod)</option>
                    <option value='std'>std</option>
                    <option value='abs_quantile'>abs quantile</option>
                  </Select>
                </Field>
                {config.band_estimator === 'abs_quantile' && (
                  <Field>
                    <KnobHelp
                      label='Band quantile'
                      help={KNOB_HELP.bandQuantile}
                    />
                    <Input
                      type='number'
                      min={0.5}
                      max={0.99}
                      step={0.01}
                      value={config.band_quantile}
                      onChange={(e) =>
                        patchConfig({
                          band_quantile: Number(e.target.value) || 0.9,
                        })
                      }
                    />
                  </Field>
                )}
                <Field>
                  <KnobHelp
                    label='Band k (multiplier)'
                    help={KNOB_HELP.bandK}
                  />
                  <Input
                    type='number'
                    min={0.5}
                    step={0.1}
                    value={config.residual_band_k}
                    onChange={(e) =>
                      patchConfig({
                        residual_band_k: Number(e.target.value) || 2,
                      })
                    }
                  />
                </Field>
                <Field>
                  <KnobHelp label='Random seed' help={KNOB_HELP.randomSeed} />
                  <Input
                    type='number'
                    value={config.random_state}
                    onChange={(e) =>
                      patchConfig({
                        random_state: Number(e.target.value) || 17,
                      })
                    }
                  />
                </Field>
                <Field>
                  <KnobHelp
                    label='Min usable / train rows'
                    help={KNOB_HELP.minRows}
                  />
                  <Row>
                    <Input
                      type='number'
                      value={config.min_usable_rows}
                      onChange={(e) =>
                        patchConfig({
                          min_usable_rows: Number(e.target.value) || 200,
                        })
                      }
                    />
                    <Input
                      type='number'
                      value={config.min_train_rows}
                      onChange={(e) =>
                        patchConfig({
                          min_train_rows: Number(e.target.value) || 100,
                        })
                      }
                    />
                  </Row>
                </Field>
              </KnobPanel>
            </KnobGrid>
          </CollapsibleSection>

          <Row>
            <Button
              disabled={
                !config.target ||
                !config.drivers.length ||
                !config.methods.length ||
                api.trainLoading
              }
              onClick={() => void onTrain()}>
              {api.trainLoading ? 'Training…' : 'Train'}
            </Button>
            {api.trainError && <Error>{api.trainError}</Error>}
          </Row>

          {(api.trainLoading || api.trainProgress.length > 0) && (
            <Group>
              <GroupTitle>Live train progress</GroupTitle>
              <ProgressLog>
                {api.trainProgress.length
                  ? api.trainProgress
                      .map((e) => e.message || e.type || e.stage || '')
                      .filter(Boolean)
                      .join('\n')
                  : 'Starting…'}
              </ProgressLog>
              {Object.keys(api.liveLossCurves).length > 0 && (
                <SizedPlot
                  $heightRem={12}
                  data={Object.entries(api.liveLossCurves).flatMap(
                    ([method, curve], i) => {
                      const color = [
                        '#22d3ee',
                        '#f97316',
                        '#a78bfa',
                        '#34d399',
                      ][i % 4]
                      const traces: Partial<PlotData>[] = [
                        {
                          x: curve.train_loss.map((_, j) => j + 1),
                          y: curve.train_loss,
                          type: 'scatter',
                          mode: 'lines',
                          name: `${method} train`,
                          line: { width: 1.3, color },
                        },
                      ]
                      if (curve.val_loss.length) {
                        traces.push({
                          x: curve.val_loss.map((_, j) => j + 1),
                          y: curve.val_loss,
                          type: 'scatter',
                          mode: 'lines',
                          name: `${method} val`,
                          line: { width: 1.1, color, dash: 'dot' },
                        })
                      }
                      return traces
                    }
                  )}
                  layout={{
                    paper_bgcolor: 'rgba(0,0,0,0)',
                    plot_bgcolor: 'rgba(0,0,0,0)',
                    margin: { l: 48, r: 12, t: 28, b: 36 },
                    legend: { font: { color: FG, size: 10 } },
                    title: {
                      text: 'Live MLP loss (streaming)',
                      font: { color: FG, size: 12 },
                      x: 0,
                    },
                    xaxis: {
                      title: {
                        text: 'epoch',
                        font: { color: FG, size: 10 },
                      },
                      tickfont: { color: FG, size: 10 },
                      gridcolor: GRID,
                    },
                    yaxis: {
                      tickfont: { color: FG, size: 10 },
                      gridcolor: GRID,
                    },
                  }}
                />
              )}
            </Group>
          )}

          <CollapsibleSection sectionKey='diagnostics' title='Diagnostics'>
            {api.trainResult && (
              <Group>
                <GroupTitle>Test residual</GroupTitle>
                <ResidualMiniPlot
                  result={api.trainResult}
                  methodKey={selectedMethodKey}
                />
              </Group>
            )}

            {api.trainResult && showOverlays && (
              <Group>
                <GroupTitle>Out-of-band points</GroupTitle>
                {outOfBandPoints.length === 0 ? (
                  <Hint>
                    No points left the expected ± band for the selected method.
                  </Hint>
                ) : (
                  <>
                    <Hint>
                      {outOfBandPoints.length} point
                      {outOfBandPoints.length === 1 ? '' : 's'} outside the band
                      (white dots on the chart). Worst offenders:
                    </Hint>
                    <OffenderList>
                      {[...outOfBandPoints]
                        .sort((a, b) => b.severity - a.severity)
                        .slice(0, 5)
                        .map((point) => (
                          <li key={point.x}>
                            {new Date(point.x).toISOString().slice(0, 16)}Z —
                            actual {point.actual.toFixed(3)} vs expected{' '}
                            {point.expected.toFixed(3)} (
                            {Number.isFinite(point.severity)
                              ? `${point.severity.toFixed(1)}× band`
                              : 'zero-width band'}
                            )
                          </li>
                        ))}
                    </OffenderList>
                  </>
                )}
              </Group>
            )}

            {api.trainResult ? (
              <DiagnosticsPanel
                result={api.trainResult}
                methodKey={selectedMethodKey}
                onMethodKeyChange={setSelectedMethodKey}
              />
            ) : (
              !api.trainLoading && (
                <Hint>
                  Hit Train to band the target pen on the chart and see metrics
                  here.
                </Hint>
              )
            )}
          </CollapsibleSection>

          <CollapsibleSection sectionKey='compareRuns' title='Compare runs'>
            <RunComparison
              runs={runs}
              onReload={(c) => {
                setConfig(c)
                setLagsText(c.feature.driver_lags.join(','))
                setHiddenText(c.mlp_hidden_layer_sizes.join(','))
                setTab('data')
              }}
              onClear={() => setRuns([])}
            />
          </CollapsibleSection>
        </>
      )}
    </Wrap>
  )
}
